import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { plannerAPI, settingsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { RefreshCw, AlertTriangle, Clock, TrendingDown, CheckCircle } from 'lucide-react';

export const Planner = () => {
  const [plan, setPlan] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [planRes, settingsRes] = await Promise.all([
        plannerAPI.getPlan(),
        settingsAPI.get(),
      ]);
      setPlan(planRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error('Failed to load planner data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getUrgencyIcon = (urgency) => {
    switch (urgency) {
      case 'critical':
        return <AlertTriangle size={16} className="text-red-500" />;
      case 'high':
        return <TrendingDown size={16} className="text-orange-500" />;
      case 'medium':
        return <Clock size={16} className="text-amber-500" />;
      default:
        return <CheckCircle size={16} className="text-green-500" />;
    }
  };

  const getUrgencyClass = (urgency) => {
    return `urgency-${urgency}`;
  };

  const criticalCount = plan.filter(p => p.urgency === 'critical').length;
  const highCount = plan.filter(p => p.urgency === 'high').length;
  const totalReorder = plan.reduce((sum, p) => sum + p.recommended_order, 0);
  const totalWarehouse = plan.reduce((sum, p) => sum + p.warehouse_stock, 0);
  const totalInbound = plan.reduce((sum, p) => sum + p.inbound_stock, 0);
  const totalAmazon = plan.reduce((sum, p) => sum + p.amazon_stock, 0);

  return (
    <Layout>
      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title" data-testid="planner-title">Replenishment Planner</h1>
          <p className="page-subtitle">Calculate product replenishment based on sales velocity</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading} data-testid="refresh-planner-btn">
          <RefreshCw size={16} className={loading ? 'animate-spin mr-2' : 'mr-2'} />
          Refresh
        </Button>
      </div>

      {/* Settings Summary */}
      {settings && (
        <Card className="mb-6 card-accent" data-testid="planner-settings-card">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-6">
              <div>
                <p className="text-xs text-slate-500 uppercase">Supplier Lead Time</p>
                <p className="font-semibold">{settings.supplier_lead_time_days} days</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Prep Time</p>
                <p className="font-semibold">{settings.prep_time_days} days</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Shipping Time</p>
                <p className="font-semibold">{settings.shipping_time_days} days</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Total Lead Time</p>
                <p className="font-bold text-blue-600">
                  {settings.supplier_lead_time_days + settings.prep_time_days + settings.shipping_time_days} days
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Target Stock Range</p>
                <p className="font-semibold">
                  {settings.target_stock_min_days} - {settings.target_stock_max_days} days
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alert Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className={criticalCount > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle size={24} className="text-red-500" />
              <div>
                <p className="text-sm text-slate-500">Critical</p>
                <p className="text-2xl font-bold text-red-600">{criticalCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={highCount > 0 ? 'border-orange-200 bg-orange-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingDown size={24} className="text-orange-500" />
              <div>
                <p className="text-sm text-slate-500">High Priority</p>
                <p className="text-2xl font-bold text-orange-600">{highCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Products Tracked</p>
            <p className="text-2xl font-bold text-slate-900">{plan.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total Units to Reorder</p>
            <p className="text-2xl font-bold text-slate-900">{totalReorder.toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Warehouse Stock</p>
            <p className="text-2xl font-bold text-slate-900">{totalWarehouse.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Inbound Stock</p>
            <p className="text-2xl font-bold text-slate-900">{totalInbound.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Amazon FBA Stock</p>
            <p className="text-2xl font-bold text-slate-900">{totalAmazon.toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="planner-table-card">
        <CardHeader>
          <CardTitle className="text-lg">Replenishment Plan</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Urgency</TableHead>
                  <TableHead>Product Name</TableHead>
                  <TableHead>ASIN</TableHead>
                  <TableHead className="text-right">Warehouse</TableHead>
                  <TableHead className="text-right">Inbound</TableHead>
                  <TableHead className="text-right">Amazon</TableHead>
                  <TableHead className="text-right">Total Stock</TableHead>
                  <TableHead className="text-right">Daily Sales</TableHead>
                  <TableHead className="text-right">Days of Cover</TableHead>
                  <TableHead className="text-right">Recommended Order</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plan.map((item) => (
                  <TableRow key={item.product_id} data-testid={`planner-row-${item.product_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getUrgencyIcon(item.urgency)}
                        <span className={`badge ${getUrgencyClass(item.urgency)}`}>
                          {item.urgency}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="font-mono text-xs">{item.asin}</TableCell>
                    <TableCell className="text-right">{item.warehouse_stock}</TableCell>
                    <TableCell className="text-right">{item.inbound_stock}</TableCell>
                    <TableCell className="text-right">{item.amazon_stock}</TableCell>
                    <TableCell className="text-right font-semibold">{item.total_stock}</TableCell>
                    <TableCell className="text-right">{item.daily_sales_rate.toFixed(1)}/day</TableCell>
                    <TableCell className="text-right">
                      <span className={(settings && item.days_of_stock < settings.target_stock_min_days) ? 'text-red-600 font-bold' : ''}>
                        {item.days_of_stock >= 999 ? '∞' : item.days_of_stock}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {item.recommended_order > 0 ? `+${item.recommended_order}` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {plan.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                      No products to plan. Add products to your database first.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </Layout>
  );
};
